import type { BusinessProfile, ParsedStripeErrors } from "../types";

export type CapabilityStatus =
  "enabled" | "disabled" | "restricted" | "paused" | "unknown";

export interface Capability {
  name: string;
  status: CapabilityStatus;
  /** Why it is not simply enabled — the stage, or the restriction. */
  detail: string | null;
}

const STATUS_STYLES = new Map<CapabilityStatus, string>([
  ["enabled", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  ["disabled", "bg-red-50 text-red-700 ring-red-200"],
  ["restricted", "bg-amber-50 text-amber-800 ring-amber-200"],
  ["paused", "bg-amber-50 text-amber-800 ring-amber-200"],
  ["unknown", "bg-muted text-muted-foreground ring-border"],
]);

export function capabilityStyle(status: CapabilityStatus): string {
  return STATUS_STYLES.get(status) ?? "";
}

/** Stripe's own capability keys, as they appear in `restrictedCapabilities`. */
const PAYMENTS_KEYS = ["card_payments"];
const PAYOUTS_KEYS = ["transfers"];

function isRestricted(
  restricted: string[] | undefined,
  keys: string[],
): boolean {
  return (restricted ?? []).some((c) => keys.includes(c));
}

/**
 * Payments, as Stripe reports it.
 *
 * A restricted capability outranks `chargesEnabled`: Stripe can leave charges
 * on while restricting the capability behind them, and the restriction is the
 * thing that will bite.
 */
function payments(
  business: BusinessProfile,
  stripeErrors: ParsedStripeErrors | null,
): Capability {
  if (isRestricted(stripeErrors?.restrictedCapabilities, PAYMENTS_KEYS)) {
    return {
      name: "Payments",
      status: "restricted",
      detail: "card_payments restricted by Stripe",
    };
  }
  if (business.stripeChargesEnabled === true) {
    return { name: "Payments", status: "enabled", detail: null };
  }
  if (business.stripeChargesEnabled === false) {
    return { name: "Payments", status: "disabled", detail: "Charges disabled" };
  }
  return { name: "Payments", status: "unknown", detail: "Not reported" };
}

/** What the payouts track's stages mean for a reader of this card. */
const PAYOUT_STAGE_REASONS = new Map<string, string>([
  ["idv_required", "Identity check not completed"],
  ["idv_processing", "Identity check under review"],
  ["idv_input_required", "Identity check needs the merchant"],
  ["bank_required", "No payout destination on file"],
]);

/**
 * Payouts, derived rather than read: there is no `payoutsEnabled` column.
 * `payoutsStage` is the denormalized payouts track, where `eligible` is the
 * hard end state, and `payoutState.pausedForReview` holds payouts even for a
 * merchant who reached it.
 */
function payouts(
  business: BusinessProfile,
  stripeErrors: ParsedStripeErrors | null,
): Capability {
  if (isRestricted(stripeErrors?.restrictedCapabilities, PAYOUTS_KEYS)) {
    return {
      name: "Payouts",
      status: "restricted",
      detail: "transfers restricted by Stripe",
    };
  }
  if (business.payoutState?.pausedForReview) {
    return { name: "Payouts", status: "paused", detail: "Paused for review" };
  }

  const stage = business.payoutsStage ?? null;
  if (stage === "eligible") {
    return { name: "Payouts", status: "enabled", detail: null };
  }
  if (stage && PAYOUT_STAGE_REASONS.has(stage)) {
    return {
      name: "Payouts",
      status: "disabled",
      detail: PAYOUT_STAGE_REASONS.get(stage) ?? stage,
    };
  }
  return {
    name: "Payouts",
    status: "unknown",
    detail: stage ? `Stage: ${stage}` : "Not reported",
  };
}

export function merchantCapabilities(
  business: BusinessProfile,
  stripeErrors: ParsedStripeErrors | null,
): Capability[] {
  return [payments(business, stripeErrors), payouts(business, stripeErrors)];
}
